import {
  Building2,
  Crown,
  Edit,
  Eye,
  FolderPlus,
  Search,
  Shield,
  Trash2,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { bugsnagClient } from "../bugsnag";
import { ANALYTICS_EVENTS, trackEvent } from "../utils/analytics";
import api from "../utils/api";

const GlobalAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [trends, setTrends] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");

  // Filters and search
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    organization: "",
    status: "",
    role: "",
    period: "30",
  });
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [showOrgTransfer, setShowOrgTransfer] = useState(null); // userId when showing transfer modal
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [createOrgData, setCreateOrgData] = useState({
    name: "",
    description: "",
    initialAdminId: "",
    type: "general",
  });
  const [createOrgLoading, setCreateOrgLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchAllOrganizationsForTransfer();
  }, []);

  // Close organization transfer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOrgTransfer && !event.target.closest(".org-transfer-dropdown")) {
        setShowOrgTransfer(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOrgTransfer]);

  useEffect(() => {
    if (activeTab === "organizations") {
      fetchOrganizations();
    } else if (activeTab === "projects") {
      fetchProjects();
    } else if (activeTab === "users") {
      fetchUsers();
      fetchAllOrganizationsForTransfer(); // Refresh organizations for transfer dropdown
    } else if (activeTab === "analytics") {
      fetchAnalytics();
    }
  }, [activeTab, filters, searchTerm]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await api.getGlobalAdminDashboard();
      setStats(data.stats);
      setRecentActivity(data.recentActivity);
      setTrends(data.trends);
    } catch (err) {
      setError(err.message);
      bugsnagClient.notify(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const params = {
        search: searchTerm,
        page: 1,
        limit: 50,
      };
      const data = await api.getAllOrganizations(params);
      setOrganizations(data.organizations);
    } catch (err) {
      console.error("Error fetching organizations:", err);
      bugsnagClient.notify(err);
    }
  };

  const fetchProjects = async () => {
    try {
      const params = {
        search: searchTerm,
        organization: filters.organization,
        status: filters.status,
        page: 1,
        limit: 50,
      };
      const data = await api.getAllProjectsAsAdmin(params);
      setProjects(data.projects);
    } catch (err) {
      console.error("Error fetching projects:", err);
      bugsnagClient.notify(err);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = {
        search: searchTerm,
        organization: filters.organization,
        role: filters.role,
        page: 1,
        limit: 50,
      };
      const data = await api.getAllUsersAsAdmin(params);
      setUsers(data.users);
    } catch (err) {
      console.error("Error fetching users:", err);
      bugsnagClient.notify(err);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const data = await api.getAnalytics(filters.period);
      setAnalytics(data);
    } catch (err) {
      console.error("Error fetching analytics:", err);
      bugsnagClient.notify(err);
    }
  };

  const handleToggleGlobalAdmin = async (userId, currentStatus) => {
    try {
      await api.toggleGlobalAdmin(userId, !currentStatus);
      fetchUsers(); // Refresh users list
      alert(
        `Global admin status ${
          !currentStatus ? "granted" : "revoked"
        } successfully`,
      );
    } catch (err) {
      alert(err.message || "Failed to update global admin status");
      bugsnagClient.notify(err);
    }
  };

  const handleUpdateOrganizationRole = async (
    userId,
    newRole,
    userName,
    orgName,
  ) => {
    try {
      await api.updateOrganizationRole(userId, newRole);
      fetchUsers(); // Refresh users list
      alert(
        `${userName}'s role in ${orgName} updated to ${newRole} successfully`,
      );
    } catch (err) {
      alert(err.message || "Failed to update organization role");
      bugsnagClient.notify(err);
    }
  };

  const fetchAllOrganizationsForTransfer = async () => {
    try {
      const data = await api.getAllOrganizations({ limit: 1000 }); // Get all organizations (increased limit)
      console.log(
        "Fetched organizations for transfer:",
        data.organizations?.length,
        data.organizations?.map((org) => ({
          name: org.name,
          memberCount: org.memberCount,
        })),
      );
      setAllOrganizations(data.organizations || []);
    } catch (err) {
      console.error("Error fetching organizations:", err);
      bugsnagClient.notify(err);
    }
  };

  const handleTransferOrganization = async (
    userId,
    orgId,
    userName,
    orgName,
  ) => {
    try {
      // Default to member role, but preserve admin role for current admins
      const targetUser = users.find((u) => u._id === userId);
      const newRole =
        targetUser?.organizationRole === "admin" ? "admin" : "member";

      await api.transferUserOrganization(userId, orgId, newRole);

      fetchUsers(); // Refresh users list
      setShowOrgTransfer(null); // Close transfer modal
      alert(`${userName} successfully transferred to ${orgName}`);
    } catch (err) {
      console.error("Transfer error:", err);
      alert(`Transfer failed: ${err.message}`);
      bugsnagClient.notify(err);
    }
  };

  const handleDeleteOrganization = async (orgId, orgName) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${orgName}"? This action cannot be undone.`,
      )
    ) {
      try {
        await api.deleteOrganization(orgId);
        fetchOrganizations();
        fetchAllOrganizationsForTransfer(); // Refresh transfer dropdown
        alert("Organization deleted successfully");
      } catch (err) {
        alert(err.message || "Failed to delete organization");
        bugsnagClient.notify(err);
      }
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();

    if (!createOrgData.name.trim()) {
      alert("Please provide organization name");
      return;
    }

    setCreateOrgLoading(true);

    try {
      await api.createOrganizationAsAdmin(createOrgData);
      trackEvent(ANALYTICS_EVENTS.ORGANIZATION_CREATED, {
        organizationName: createOrgData.name,
        type: createOrgData.type,
      });

      // Reset form and close modal
      setCreateOrgData({
        name: "",
        description: "",
        initialAdminId: "",
        type: "general",
      });
      setShowCreateOrg(false);

      // Refresh organizations, users list, and transfer dropdown
      fetchOrganizations();
      fetchUsers();
      fetchAllOrganizationsForTransfer();

      alert("Organization created successfully");
    } catch (err) {
      alert(err.message || "Failed to create organization");
      bugsnagClient.notify(err);
    } finally {
      setCreateOrgLoading(false);
    }
  };

  const resetCreateOrgForm = () => {
    setCreateOrgData({
      name: "",
      description: "",
      initialAdminId: "",
      type: "general",
    });
    setShowCreateOrg(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800";
      case "In Progress":
        return "bg-blue-100 text-blue-800";
      case "Planning":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "manager":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error loading dashboard</h3>
          <p className="text-red-600 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Crown className="mr-3 text-yellow-500" />
          Global Admin Dashboard
        </h1>
        <p className="text-gray-600 mt-1">Platform oversight and management</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {["dashboard", "organizations", "projects", "users", "analytics"].map(
            (tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ),
          )}
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && stats && (
        <div className="space-y-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Total Users
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalUsers}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Organizations
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalOrganizations}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FolderPlus className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Total Projects
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalProjects}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Shield className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Global Admins
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.globalAdmins}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          {recentActivity && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow border">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">
                    Recent Users
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {recentActivity.users.slice(0, 5).map((user) => (
                      <div
                        key={user._id}
                        className="flex items-center justify-between"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {user.name}
                          </p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                          <p className="text-xs text-gray-400">
                            {user.organization?.name || "No organization"}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow border">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">
                    Recent Organizations
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {recentActivity.organizations.slice(0, 5).map((org) => (
                      <div
                        key={org._id}
                        className="flex items-center justify-between"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {org.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            Owner: {org.owner?.name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {org.members.length} members
                          </p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(org.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow border">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">
                    Recent Projects
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {recentActivity.projects.slice(0, 5).map((project) => (
                      <div
                        key={project._id}
                        className="flex items-center justify-between"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {project.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {project.organization?.name}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(project.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Organizations Tab */}
      {activeTab === "organizations" && (
        <div className="space-y-6">
          {/* Search and Filters */}
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center justify-between space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search organizations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                </div>
              </div>
              <button
                onClick={() => setShowCreateOrg(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <Building2 className="w-4 h-4" />
                <span>Create Organization</span>
              </button>
            </div>
          </div>

          {/* Organizations Table */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Members
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Projects
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {organizations.map((org) => (
                    <tr key={org._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {org.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {org.description || "No description"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {org.memberCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {org.projectCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(org.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button className="text-blue-600 hover:text-blue-900">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="text-green-600 hover:text-green-900">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteOrganization(org._id, org.name)
                            }
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Projects Tab */}
      {activeTab === "projects" && (
        <div className="space-y-6">
          {/* Search and Filters */}
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Projects Table */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Project
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Owner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {projects.map((project) => (
                    <tr key={project._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {project.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            {project.location}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {project.organization?.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {project.owner?.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {project.owner?.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                        {project.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(project.lastUpdated).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === "users" && (
        <div className="space-y-6">
          {/* Search and Filters */}
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                </div>
              </div>
              <select
                value={filters.role}
                onChange={(e) =>
                  setFilters({ ...filters, role: e.target.value })
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Org Roles</option>
                <option value="admin">Org Admin</option>
                <option value="member">Org Member</option>
              </select>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Org Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Global Admin
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Active
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                          </div>
                          {user.isGlobalAdmin && (
                            <Crown className="w-4 h-4 text-yellow-500 ml-2" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm relative">
                        {user.organization ? (
                          <div className="relative">
                            {user.isOrganizationOwner ? (
                              <div className="flex items-center">
                                <span className="text-gray-900">
                                  {user.organization.name}
                                </span>
                                <Crown
                                  className="w-3 h-3 text-yellow-500 ml-1"
                                  title="Organization Owner - Cannot transfer"
                                />
                              </div>
                            ) : (
                              <button
                                onClick={() =>
                                  setShowOrgTransfer(
                                    showOrgTransfer === user._id
                                      ? null
                                      : user._id,
                                  )
                                }
                                className="text-gray-900 hover:text-blue-600 cursor-pointer underline"
                              >
                                {user.organization.name}
                              </button>
                            )}
                            {showOrgTransfer === user._id &&
                              !user.isOrganizationOwner && (
                                <div className="org-transfer-dropdown absolute z-10 mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg">
                                  <div className="p-2">
                                    <div className="text-xs text-gray-500 mb-2">
                                      Transfer to:
                                    </div>
                                    <div className="max-h-32 overflow-y-auto">
                                      {allOrganizations
                                        .filter(
                                          (org) =>
                                            org._id !== user.organization._id,
                                        )
                                        .map((org) => (
                                          <button
                                            key={org._id}
                                            onClick={() =>
                                              handleTransferOrganization(
                                                user._id,
                                                org._id,
                                                user.name,
                                                org.name,
                                              )
                                            }
                                            className="block w-full text-left px-2 py-1 text-sm hover:bg-gray-100 rounded"
                                          >
                                            {org.name}
                                          </button>
                                        ))}
                                    </div>
                                    <button
                                      onClick={() => setShowOrgTransfer(null)}
                                      className="mt-2 w-full px-2 py-1 text-xs text-gray-500 hover:text-gray-700 border-t border-gray-200"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                          </div>
                        ) : (
                          <div className="relative">
                            <button
                              onClick={() =>
                                setShowOrgTransfer(
                                  showOrgTransfer === user._id
                                    ? null
                                    : user._id,
                                )
                              }
                              className="text-gray-500 hover:text-blue-600 cursor-pointer underline"
                            >
                              No organization
                            </button>
                            {showOrgTransfer === user._id && (
                              <div className="org-transfer-dropdown absolute z-10 mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg">
                                <div className="p-2">
                                  <div className="text-xs text-gray-500 mb-2">
                                    Assign to:
                                  </div>
                                  <div className="max-h-32 overflow-y-auto">
                                    {allOrganizations.map((org) => (
                                      <button
                                        key={org._id}
                                        onClick={() =>
                                          handleTransferOrganization(
                                            user._id,
                                            org._id,
                                            user.name,
                                            org.name,
                                          )
                                        }
                                        className="block w-full text-left px-2 py-1 text-sm hover:bg-gray-100 rounded"
                                      >
                                        {org.name}
                                      </button>
                                    ))}
                                  </div>
                                  <button
                                    onClick={() => setShowOrgTransfer(null)}
                                    className="mt-2 w-full px-2 py-1 text-xs text-gray-500 hover:text-gray-700 border-t border-gray-200"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.organization ? (
                          <select
                            value={user.organizationRole || "member"}
                            onChange={(e) =>
                              handleUpdateOrganizationRole(
                                user._id,
                                e.target.value,
                                user.name,
                                user.organization.name,
                              )
                            }
                            className={`px-2 py-1 rounded text-xs font-medium border-0 ${getRoleColor(
                              user.organizationRole || "member",
                            )} cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                            No org
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() =>
                            handleToggleGlobalAdmin(
                              user._id,
                              user.isGlobalAdmin,
                            )
                          }
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            user.isGlobalAdmin
                              ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                              : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                          }`}
                        >
                          {user.isGlobalAdmin ? "Revoke" : "Grant"}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.lastActive
                          ? new Date(user.lastActive).toLocaleDateString()
                          : "Never"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button className="text-blue-600 hover:text-blue-900">
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === "analytics" && analytics && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Platform Analytics
              </h3>
              <select
                value={filters.period}
                onChange={(e) =>
                  setFilters({ ...filters, period: e.target.value })
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {analytics.analytics.userGrowth.length}
                </p>
                <p className="text-sm text-gray-500">New Users</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {analytics.analytics.organizationGrowth.length}
                </p>
                <p className="text-sm text-gray-500">New Organizations</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">
                  {analytics.analytics.projectGrowth.length}
                </p>
                <p className="text-sm text-gray-500">New Projects</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">
                  {analytics.analytics.activeUsers}
                </p>
                <p className="text-sm text-gray-500">Active Users</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Organization Modal */}
      {showCreateOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Create New Organization
              </h3>
            </div>

            <form
              onSubmit={handleCreateOrganization}
              className="px-6 py-4 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Name *
                </label>
                <input
                  type="text"
                  value={createOrgData.name}
                  onChange={(e) =>
                    setCreateOrgData({ ...createOrgData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter organization name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={createOrgData.description}
                  onChange={(e) =>
                    setCreateOrgData({
                      ...createOrgData,
                      description: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter organization description"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Type
                </label>
                <select
                  value={createOrgData.type}
                  onChange={(e) =>
                    setCreateOrgData({ ...createOrgData, type: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="general">General</option>
                  <option value="quantity_surveyor">Quantity Surveyor</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose the organization type. This controls feature
                  availability.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Initial Admin (Optional)
                </label>
                <select
                  value={createOrgData.initialAdminId}
                  onChange={(e) =>
                    setCreateOrgData({
                      ...createOrgData,
                      initialAdminId: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No initial admin (assign later)</option>
                  {users
                    .filter((user) => !user.isOrganizationOwner)
                    .map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name} ({user.email})
                        {user.organization
                          ? ` - Currently in ${user.organization.name}`
                          : " - No organization"}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Optional: Select a user to automatically become the first
                  admin. They will be transferred from their current
                  organization if needed.
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetCreateOrgForm}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={createOrgLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-blue-400"
                  disabled={createOrgLoading}
                >
                  {createOrgLoading ? "Creating..." : "Create Organization"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalAdminDashboard;
